import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Eye, BookOpen, LayoutTemplate, X } from 'lucide-react';

const MODES = [
  { id: 'normal',    icon: LayoutTemplate, key: 'accessibility.standard'  },
  { id: 'dyslexia',  icon: BookOpen,        key: 'accessibility.dyslexia'  },
  { id: 'lowvision', icon: Eye,             key: 'accessibility.lowvision' },
];

const applyMode = (mode) => {
  document.body.classList.remove('a11y-dyslexia', 'a11y-lowvision');
  if (mode !== 'normal') document.body.classList.add(`a11y-${mode}`);
};

const AccessibilityWidget = () => {
  const { t } = useTranslation();
  const [open, setOpen]             = useState(false);
  const [mode, setMode]             = useState('normal');
  const [persist, setPersist]       = useState(false);
  const [panelPosition, setPanelPosition] = useState('bottom');
  const panelRef = useRef(null);

  // Restore saved mode on mount
  useEffect(() => {
    const saved = localStorage.getItem('a11y_mode');
    if (saved && saved !== 'normal') {
      setMode(saved);
      setPersist(true);
      applyMode(saved);
    }
  }, []);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Listen for toggle events dispatched by the header button
  useEffect(() => {
    const handler = (e) => {
      const pos = e.detail?.position || 'bottom';
      setPanelPosition(pos);
      setOpen(o => !o);
    };
    document.addEventListener('toggleA11y', handler);
    return () => document.removeEventListener('toggleA11y', handler);
  }, []);

  const handleMode = (id) => {
    setMode(id);
    applyMode(id);
    if (persist) {
      if (id === 'normal') localStorage.removeItem('a11y_mode');
      else localStorage.setItem('a11y_mode', id);
    }
    setOpen(false);
  };

  const handlePersist = (checked) => {
    setPersist(checked);
    if (checked && mode !== 'normal') localStorage.setItem('a11y_mode', mode);
    else localStorage.removeItem('a11y_mode');
  };

  const panelCls = panelPosition === 'top'
    ? 'fixed top-16 right-4 z-50'
    : 'fixed bottom-20 right-4 z-50';

  return (
    <>
      {/* Floating panel — rendered in a portal so fixed positioning is never affected by ancestor transforms */}
      {open && createPortal(
        <div ref={panelRef} className={`${panelCls} bg-white rounded-xl shadow-xl border-2 border-ter-blue w-64 p-4`}>
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {t('accessibility.title')}
            </span>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-1">
            {MODES.map(({ id, icon: Icon, key }) => (
              <button
                key={id}
                onClick={() => handleMode(id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  mode === id
                    ? 'bg-ter-blue text-white'
                    : 'hover:bg-slate-100 text-slate-700'
                }`}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {t(key)}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={persist}
              onChange={(e) => handlePersist(e.target.checked)}
              className="rounded"
            />
            {t('accessibility.persist')}
          </label>
        </div>,
        document.body
      )}

      {/* Floating trigger button — always visible bottom-right */}
      <button
        onClick={() => { setPanelPosition('bottom'); setOpen(v => !v); }}
        aria-label={t('accessibility.title')}
        title={t('accessibility.title')}
        className="fixed bottom-4 right-4 z-50 w-12 h-12 rounded-full bg-ter-blue text-white border-2 border-white shadow-lg flex items-center justify-center hover:bg-ter-blue/90 transition-colors text-xl"
      >
        ♿
      </button>
    </>
  );
};

export default AccessibilityWidget;
